import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import ActivityEditor from '../../../activity-editor/ActivityEditor';
import { getCurrentLocale, useTranslation } from '../../../../shared/i18n';

/**
 * ActivityEditorWrapper - Wraps ActivityEditor for use in React Router
 * Fetches activity data from API and handles navigation callbacks
 */
export const ActivityEditorWrapper = () => {
  const { childId, activityId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [activityData, setActivityData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get CSRF token from meta tag
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';

  // Get Rails environment from meta tag or default to production
  const railsEnv = document.querySelector('meta[name="rails-env"]')?.content || 'production';

  // Fetch activity data
  useEffect(() => {
    const fetchActivityData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`/api/children/${childId}/curriculum/activities/${activityId}`, {
          headers: {
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch activity');
        }

        const data = await response.json();
        setActivityData(data);
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    };

    if (childId && activityId) {
      fetchActivityData();
    }
  }, [childId, activityId]);

  // Handle activity update completion
  const handleComplete = (activity) => {
    // Invalidate the curriculum cache so it refetches after navigation
    queryClient.invalidateQueries({ queryKey: ['curriculum', childId] });
    // Navigate to curriculum page with success notice
    navigate(`/children/${childId}/curriculum?notice=Activity+updated+successfully`);
  };

  // Handle cancellation
  const handleCancel = () => {
    // Navigate back to curriculum page
    navigate(`/children/${childId}/curriculum`);
  };

  if (isLoading) {
    return (
      <div className="bg-light min-vh-100">
        <div className="container py-4">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-light min-vh-100">
        <div className="container py-4">
          <div className="alert alert-danger">
            {t('common.error')}: {error}
          </div>
          <button className="btn btn-outline-secondary" onClick={handleCancel}>
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  if (!activityData) {
    return null;
  }

  const { child, activity } = activityData;

  return (
    <ActivityEditor
      childId={child.id}
      childName={child.username}
      activityId={activity.id}
      activityType={activity.activity_type}
      activityName={activity.name}
      iconUrl={activity.icon_url}
      configFields={activity.config_fields}
      existingCredentials={activity.existing_credentials}
      existingCourses={activity.existing_courses}
      existingGroupAlias={activity.existing_group_alias}
      existingConfig={activity.existing_config}
      existingVolume={activity.existing_volume}
      courseStructure={activity.course_structure}
      isExternalService={activity.is_external_service}
      railsEnv={railsEnv}
      csrfToken={csrfToken}
      locale={getCurrentLocale()}
      onComplete={handleComplete}
      onCancel={handleCancel}
    />
  );
};

export default ActivityEditorWrapper;
